import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { isEmpty, pickBy } from 'lodash';
import { ERROR_409 } from 'common/config/api';
import { FIRM_SUMMARY_QUERY_KEY, FUND_LIST_BY_FIRM_QUERY_KEY } from 'common/constants/services/firms';
import {
  CREATE_FUND_DEFAULT_ERROR_MESSAGE,
  CREATE_FUND_DEFAULT_SUCCESS_MESSAGE,
  CREATE_FUND_NAME_ALREADY_EXISTS_ERROR_MESSAGE,
} from 'common/constants/services/funds';
import { SuperAgentError } from 'common/types/services';
import { FundService } from 'services';
import { getNumberValue, getStringValue, throwError } from 'utilities';
import { CreateFundParams, CreateFundResponse } from './useCreateFund.types';

const createFund = async (params: CreateFundParams): Promise<CreateFundResponse | null> => {
  const { fund } = params;

  if (isEmpty(pickBy(fund))) {
    return throwError(CREATE_FUND_DEFAULT_ERROR_MESSAGE);
  }

  const fundService = new FundService();

  const errorMessages: { [status: number]: string } = {
    [ERROR_409]: CREATE_FUND_NAME_ALREADY_EXISTS_ERROR_MESSAGE,
  };

  try {
    const createdFund = (await fundService.newFund(fund)) as CreateFundResponse['fund'];

    if (createdFund?.id) {
      return {
        fund: createdFund,
        responseMessage: CREATE_FUND_DEFAULT_SUCCESS_MESSAGE(getStringValue(createdFund?.name)),
      } as CreateFundResponse;
    }

    return throwError(CREATE_FUND_DEFAULT_ERROR_MESSAGE);
  } catch (error) {
    const { status } = error as SuperAgentError;

    return throwError(errorMessages[getNumberValue(status)] || CREATE_FUND_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useCreateFund = () => {
  const queryClient = useQueryClient();

  const createFundMutation: UseMutationResult<CreateFundResponse | null, Error, CreateFundParams> = useMutation({
    mutationFn: createFund,
  });

  const { isSuccess, isError, mutateAsync } = createFundMutation;

  if (isSuccess) {
    // Invalidate the queries to refetch the updated data of the Fund
    queryClient.invalidateQueries({ queryKey: [FIRM_SUMMARY_QUERY_KEY] });
    queryClient.invalidateQueries({ queryKey: [FUND_LIST_BY_FIRM_QUERY_KEY] });

    // Clean the mutation internal state
    createFundMutation.reset();
  }

  return {
    isError,
    mutateAsync,
  };
};

export default useCreateFund;
